import React, { useState, useEffect } from 'react';
import {
  Clock,
  TrendingUp,
  CreditCard,
  Building2,
  CheckCircle2,
  AlertCircle,
  X,
  Lock,
  ArrowRight,
  Wallet,
} from 'lucide-react';
import { ProfessionalEarningsSummary, NigerianBankOption } from '../../types';
import {
  requestProfessionalPayoutApi,
  fetchNigerianBanksApi,
  resolveProfessionalBankAccountApi,
  saveProfessionalBankAccountApi,
} from '../../lib/supabaseService';

interface EarningsSummaryCardProps {
  professionalId: string;
  summary: ProfessionalEarningsSummary;
  onRefresh?: () => void;
  onRequestPayoutTab?: () => void;
  className?: string;
}

export const EarningsSummaryCard: React.FC<EarningsSummaryCardProps> = ({
  professionalId,
  summary,
  onRefresh,
  onRequestPayoutTab,
  className = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [banks, setBanks] = useState<NigerianBankOption[]>([]);
  const [bankCode, setBankCode] = useState<string>(summary.bankAccount?.bankCode || '');
  const [bankName, setBankName] = useState<string>(summary.bankAccount?.bankName || '');
  const [accountNumber, setAccountNumber] = useState<string>(summary.bankAccount?.accountNumber || '');
  const [accountName, setAccountName] = useState<string>(summary.bankAccount?.accountName || '');
  const [isResolvingBank, setIsResolvingBank] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const availableBalance = summary.availableEarnings || 0;
  const pendingEarnings = summary.pendingEarnings || 0;
  const totalEarnings = summary.totalProfessionalEarnings || 0;
  const paidOutEarnings = summary.paidOutEarnings || 0;
  const processingPayouts = summary.processingPayouts || 0;
  const hasVerifiedBank = Boolean(
    summary.bankAccount?.isVerified &&
      summary.bankAccount?.recipientCode &&
      summary.bankAccount?.accountNumber
  );

  useEffect(() => {
    if (summary.bankAccount) {
      setBankCode(summary.bankAccount.bankCode || '');
      setBankName(summary.bankAccount.bankName || '');
      setAccountNumber(summary.bankAccount.accountNumber || '');
      setAccountName(summary.bankAccount.accountName || '');
    }
  }, [summary.bankAccount]);

  const handleOpenModal = async () => {
    setAmount(availableBalance > 0 ? String(availableBalance) : '');
    setIdempotencyKey(`wd_${professionalId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    setFeedback(null);
    setIsModalOpen(true);
    if (banks.length === 0) {
      const bankListRes = await fetchNigerianBanksApi();
      if (bankListRes.banks?.length) {
        setBanks(bankListRes.banks);
      }
    }
  };

  const handleVerifyAccount = async (targetBankCode: string, targetAcctNum: string) => {
    const cleanNum = targetAcctNum.replace(/\D/g, '');
    if (!targetBankCode || cleanNum.length !== 10) {
      setAccountName('');
      return;
    }
    setIsResolvingBank(true);
    setAccountName('');
    setFeedback(null);
    try {
      const res = await resolveProfessionalBankAccountApi({
        professionalId,
        bankCode: targetBankCode,
        accountNumber: cleanNum,
      });
      if (res.success && res.accountName) {
        setAccountName(res.accountName);
      } else {
        setAccountName('');
        if (res.error) {
          setFeedback({ type: 'error', message: res.error });
        }
      }
    } catch (err: any) {
      setAccountName('');
      setFeedback({ type: 'error', message: err.message || 'Could not resolve bank account.' });
    } finally {
      setIsResolvingBank(false);
    }
  };

  const handleSubmitPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const numAmount = Number(amount);

    if (isNaN(numAmount) || numAmount < 1000) {
      setFeedback({
        type: 'error',
        message: 'Minimum withdrawal amount is ₦1,000.',
      });
      return;
    }

    if (numAmount > availableBalance) {
      setFeedback({
        type: 'error',
        message: `Withdrawal amount exceeds your current available balance of ₦${availableBalance.toLocaleString()}.`,
      });
      return;
    }

    const cleanAcct = accountNumber.trim().replace(/\D/g, '');
    if (!cleanAcct || cleanAcct.length !== 10) {
      setFeedback({
        type: 'error',
        message: 'Please provide a valid 10-digit NUBAN Nigerian bank account number.',
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);

    try {
      // Ensure bank account is verified and has a Paystack recipient code if modified
      if (
        !hasVerifiedBank ||
        cleanAcct !== summary.bankAccount?.accountNumber ||
        (bankCode && bankCode !== summary.bankAccount?.bankCode)
      ) {
        if (!bankCode) {
          setFeedback({
            type: 'error',
            message: 'Please select your Nigerian bank so Paystack can verify your account.',
          });
          setIsSubmitting(false);
          return;
        }
        const saveRes = await saveProfessionalBankAccountApi({
          professionalId,
          bankCode,
          bankName: bankName.trim(),
          accountNumber: cleanAcct,
        });
        if (!saveRes.success) {
          setFeedback({
            type: 'error',
            message: saveRes.error || 'Could not verify bank account with Paystack.',
          });
          setIsSubmitting(false);
          return;
        }
      }

      const res = await requestProfessionalPayoutApi({
        professionalId,
        amount: numAmount,
        bankCode: bankCode || summary.bankAccount?.bankCode,
        bankName: bankName.trim() || summary.bankAccount?.bankName,
        accountNumber: cleanAcct,
        accountName: accountName.trim() || summary.bankAccount?.accountName,
        idempotencyKey,
      });

      if (res.success) {
        setFeedback({
          type: 'success',
          message:
            res.message ||
            'Withdrawal initiated via Paystack Transfers! Status will update automatically upon bank confirmation.',
        });

        if (onRefresh) onRefresh();

        setTimeout(() => {
          setIsModalOpen(false);
          if (onRequestPayoutTab) onRequestPayoutTab();
        }, 2200);
      } else {
        // Generate a fresh idempotency key only if the request failed validation so user can fix & retry
        setIdempotencyKey(`wd_${professionalId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
        setFeedback({
          type: 'error',
          message: res.error || 'Failed to initiate withdrawal.',
        });
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err.message || 'Network error processing withdrawal request.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 text-white rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-xl relative overflow-hidden space-y-6 ${className}`}>
      {/* Background Decorative Mesh */}
      <div className="absolute top-0 right-0 -mt-10 -mr-10 w-60 h-60 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-60 h-60 rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />

      {/* Card Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800/80 relative z-10">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <Wallet className="w-3.5 h-3.5 text-emerald-400" />
            <span>Artisan Financial Summary (90% Net Share)</span>
          </div>
          <h2 className="text-lg sm:text-xl font-black text-white">Earnings & Balance Overview</h2>
        </div>

        <button
          type="button"
          onClick={handleOpenModal}
          disabled={availableBalance < 1000}
          className="px-5 py-2.5 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shrink-0 min-h-[44px]"
        >
          <CreditCard className="w-4 h-4 text-slate-950" />
          <span>Withdraw to Bank</span>
        </button>
      </div>

      {/* Main Financial Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 relative z-10">
        {/* Available Account Balance */}
        <div className="p-5 rounded-2xl bg-slate-800/80 border border-slate-700/80 space-y-2 hover:border-emerald-500/50 transition-colors">
          <div className="flex items-center justify-between text-xs font-bold text-emerald-400">
            <span>Available to Withdraw</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <p className="text-2xl sm:text-3xl font-black text-white font-mono">
            ₦{availableBalance.toLocaleString()}
          </p>
          <p className="text-[11px] text-slate-400 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
            <span>Released net earnings (10% fee already deducted)</span>
          </p>
        </div>

        {/* Pending Escrow Earnings */}
        <div className="p-5 rounded-2xl bg-slate-800/80 border border-slate-700/80 space-y-2 hover:border-amber-500/50 transition-colors">
          <div className="flex items-center justify-between text-xs font-bold text-amber-400">
            <span>Pending Escrow Earnings</span>
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-2xl sm:text-3xl font-black text-amber-300 font-mono">
            ₦{pendingEarnings.toLocaleString()}
          </p>
          <p className="text-[11px] text-slate-400 flex items-center gap-1">
            <Lock className="w-3 h-3 text-amber-400 shrink-0" />
            <span>Held in escrow awaiting customer confirmation</span>
          </p>
        </div>

        {/* Total Lifetime Earnings */}
        <div className="p-5 rounded-2xl bg-slate-800/80 border border-slate-700/80 space-y-2 hover:border-blue-500/50 transition-colors">
          <div className="flex items-center justify-between text-xs font-bold text-blue-400">
            <span>Total Net Earnings (90%)</span>
            <TrendingUp className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-2xl sm:text-3xl font-black text-blue-300 font-mono">
            ₦{totalEarnings.toLocaleString()}
          </p>
          <p className="text-[11px] text-slate-400">
            Lifetime released artisan earnings on JobMatch NG
          </p>
        </div>
      </div>

      {/* Sub-Footer Info Row */}
      <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-400 relative z-10 border-t border-slate-800/60">
        <div className="flex flex-wrap items-center gap-4">
          <span>Paid Out: <strong className="text-slate-200">₦{paidOutEarnings.toLocaleString()}</strong></span>
          {processingPayouts > 0 && (
            <>
              <span>•</span>
              <span>Processing Transfer: <strong className="text-amber-300">₦{processingPayouts.toLocaleString()}</strong></span>
            </>
          )}
          <span>•</span>
          <span>Completed Jobs: <strong className="text-slate-200">{summary.totalJobsCompleted || 0}</strong></span>
          {hasVerifiedBank && (
            <>
              <span>•</span>
              <span className="inline-flex items-center gap-1 text-emerald-400 font-bold">
                <Building2 className="w-3.5 h-3.5" />
                {summary.bankAccount?.bankName} ({summary.bankAccount?.accountNumber})
              </span>
            </>
          )}
        </div>

        {onRequestPayoutTab && (
          <button
            type="button"
            onClick={onRequestPayoutTab}
            className="text-emerald-400 font-bold hover:underline inline-flex items-center gap-1 cursor-pointer"
          >
            <span>Manage Bank Account & Withdrawals</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Interactive Withdraw to Bank Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white text-slate-900 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 relative space-y-5">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1 pr-6">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2">
                <CreditCard className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-black text-slate-900">Withdraw to Bank</h3>
              <p className="text-xs text-slate-500">
                Transfer your available net earnings directly to your verified Nigerian bank account via Paystack Transfers. No extra commission is deducted on withdrawal.
              </p>
            </div>

            {feedback && (
              <div
                className={`p-3.5 rounded-2xl flex items-center gap-2 text-xs font-bold ${
                  feedback.type === 'success'
                    ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                    : 'bg-rose-50 text-rose-900 border border-rose-200'
                }`}
              >
                {feedback.type === 'success' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                )}
                <span>{feedback.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmitPayout} className="space-y-4">
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between text-xs">
                <span className="font-bold text-slate-600">Available to Withdraw:</span>
                <span className="font-mono font-black text-emerald-700 text-sm">
                  ₦{availableBalance.toLocaleString()}
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Withdrawal Amount (₦)
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 25000"
                  max={availableBalance}
                  min={1000}
                  className="w-full p-3 rounded-2xl border border-slate-200 text-sm font-mono font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Select Nigerian Bank
                </label>
                <select
                  value={bankCode}
                  onChange={(e) => {
                    const selectedCode = e.target.value;
                    setBankCode(selectedCode);
                    setAccountName('');
                    const matched = banks.find((b) => b.code === selectedCode);
                    if (matched) setBankName(matched.name);
                    if (accountNumber.replace(/\D/g, '').length === 10 && selectedCode) {
                      handleVerifyAccount(selectedCode, accountNumber);
                    }
                  }}
                  className="w-full p-3 rounded-2xl border border-slate-200 text-xs text-slate-900 bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                  required
                >
                  <option value="">-- Choose Your Bank --</option>
                  {banks.map((b) => (
                    <option key={`${b.code}-${b.name}`} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  10-Digit NUBAN Account Number
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setAccountNumber(val);
                    if (val.length === 10 && bankCode) {
                      handleVerifyAccount(bankCode, val);
                    } else {
                      setAccountName('');
                    }
                  }}
                  placeholder="0123456789"
                  maxLength={10}
                  className="w-full p-3 rounded-2xl border border-slate-200 text-xs font-mono text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Verified Account Name {isResolvingBank && <span className="text-emerald-600">(Verifying with Paystack...)</span>}
                </label>
                <input
                  type="text"
                  value={accountName}
                  readOnly
                  placeholder={
                    isResolvingBank
                      ? 'Resolving account holder name via Paystack...'
                      : 'Resolved automatically via Paystack'
                  }
                  className={`w-full p-3 rounded-2xl border text-xs font-bold focus:outline-hidden ${
                    accountName
                      ? 'border-emerald-300 bg-emerald-50/60 text-emerald-950'
                      : 'border-slate-200 bg-slate-50 text-slate-900'
                  }`}
                  required
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                  className="flex-1 py-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isResolvingBank || availableBalance < 1000}
                  className="flex-1 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md transition-all cursor-pointer disabled:opacity-50 min-h-[44px]"
                >
                  {isSubmitting ? 'Processing Transfer...' : 'Withdraw to Bank'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

